// External Dependencies
import { useEffect, useRef, useState } from 'react';
import { Heading, useDisclosure, useOutsideClick } from '@chakra-ui/react';
import { FeatureFlagNameEnum } from '@keyops-cep/api-client';

// Internal Dependencies
import ErrorComponent from '../../../../components/ErrorComponent';
import { FilterGroupsType } from '../../../../HOCs/Filter/FilterProvider';
import useFilterProvider from '../../../../HOCs/Filter/useFilterProvider';
import useBEFeatureFlag from '../../../../hooks/useBEFeatureFlag';
import useIsAdmin from '../../../../hooks/useIsAdmin';
import { ENGAGEMENT_STATUSES } from '../../../../utils/dto/engagement.dto';
import i18n from '../../../../utils/i18n';
import AnalysisConclusion from '../../components/AnalysisConclusion';
import Chat from '../../components/chat/Chat';
import DownloadButton from '../../components/DownloadButton';
import EngagementAnalysisIntro from '../../components/EngagementAnalysisIntro';
import FiltersDrawer from '../../components/FiltersDrawer';
import ScrollButton from '../../components/ScrollButton';
import InsightsEngagementDetails from '../../InsightsEngagementDetails';
import KPISection from '../../KPISection';
import RowDataEngagementDetails from '../../RowDataEngagementDetails';

const EngagementDetailsResults = ({
  adBoardSurvey = false,
}: {
  adBoardSurvey?: boolean;
}) => {
  const isAdmin = useIsAdmin();
  const { engagementDetails, filterGroups } = useFilterProvider();
  const { id: engagementId, tenantId } = engagementDetails;

  const [isFilterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false);
  const [localFilterGroups, setLocalFilterGroups] = useState<
    FilterGroupsType[]
  >([]);

  useEffect(() => {
    setLocalFilterGroups(filterGroups);
  }, [filterGroups]);

  const toggleFilterDrawer = () => setFilterDrawerOpen((prev) => !prev);

  // Handle EngageAI chat logic
  const {
    isOpen: isChatOpen,
    onOpen: onChatOpen,
    onClose: onChatClose,
  } = useDisclosure();
  const chatRef = useRef<HTMLDivElement>(null);
  useOutsideClick({
    ref: chatRef,
    handler: () => {
      onChatClose();
    },
  });

  const { data: isEngageAIActive, isLoading: isFlagDataLoading } =
    useBEFeatureFlag(tenantId, FeatureFlagNameEnum.EngageAIChat);

  // Show error component if there are no questions
  if (
    !Array.isArray(engagementDetails?.questions) ||
    !engagementDetails.questions.length
  )
    return <ErrorComponent />;

  return (
    <>
      <div
        id="engagement-details-results"
        data-testid="engagement-details-results-test-id"
        style={{ position: 'relative' }}
      >
        {engagementDetails.discriminator === 'EvaluationEventEngagement' ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <DownloadButton engagementData={engagementDetails} />
          </div>
        ) : (
          <KPISection
            engagementDetails={engagementDetails}
            adBoardSurvey={adBoardSurvey}
          />
        )}
        <Heading
          as="h2"
          fontSize={'xl'}
          color={'gray.500'}
          textTransform={'capitalize'}
          aria-label="insights-title"
          mt={8}
          mb={4}
        >
          {!adBoardSurvey && i18n.t('engagementDetails.insights')}
        </Heading>
        {engagementDetails.analysis?.intro && (
          <EngagementAnalysisIntro intro={engagementDetails.analysis?.intro} />
        )}
        {!isAdmin &&
          engagementDetails.resultsStatus ===
            ENGAGEMENT_STATUSES.RAW_DATA_AVAILABLE && (
            <RowDataEngagementDetails />
          )}
        {(isAdmin ||
          engagementDetails.resultsStatus ===
            ENGAGEMENT_STATUSES.INSIGHTS_AVAILABLE) && (
          <InsightsEngagementDetails
            toggleFilterDrawer={toggleFilterDrawer}
            localFilterGroups={localFilterGroups}
          />
        )}
        <AnalysisConclusion engagementDetails={engagementDetails} />
      </div>
      {/* CHAT */}
      {engagementId &&
        engagementDetails.resultsStatus ===
          ENGAGEMENT_STATUSES.INSIGHTS_AVAILABLE &&
        isEngageAIActive &&
        !isFlagDataLoading &&
        // do not display if survey in a kolab
        engagementDetails.discriminator === 'SurveyEngagement' &&
        !engagementDetails.adBoardEngagementId && (
          <Chat
            engagementId={engagementId}
            engagementStatus={engagementDetails.resultsStatus}
            isEngageAIActive={isEngageAIActive}
            isChatOpen={isChatOpen}
            onClose={onChatClose}
            onOpen={onChatOpen}
            chatRef={chatRef}
          />
        )}
      <FiltersDrawer
        isFilterDrawerOpen={isFilterDrawerOpen}
        toggleFilterDrawer={toggleFilterDrawer}
        localFilterGroups={localFilterGroups}
        setLocalFilterGroups={setLocalFilterGroups}
      />
      <ScrollButton
        engagementStatus={engagementDetails.resultsStatus}
        toggleFilterDrawer={toggleFilterDrawer}
      />
    </>
  );
};

export default EngagementDetailsResults;
